import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ChevronLeft, ChevronRight, Search, Users, Loader2, KeyRound, XCircle, Copy, Check, Filter, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { formatDistanceToNow } from 'date-fns';
import { Logger } from '@/lib/utils/logger';

interface AuthUser {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  email_confirmed_at: string | null;
  full_name: string | null;
  roles: string[];
  has_subscription: boolean;
  stripe_customer_id: string | null;
}

const logger = new Logger('UserManagement');

export default function UserManagement() {
  const [users, setUsers] = useState<AuthUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [premiumFilter, setPremiumFilter] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<AuthUser | null>(null);
  const [copiedId, setCopiedId] = useState(false);
  const [sortColumn, setSortColumn] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [passwordDialog, setPasswordDialog] = useState<{
    open: boolean;
    userId: string;
    email: string;
  }>({ open: false, userId: '', email: '' });
  const [newPassword, setNewPassword] = useState('');
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    userId: string;
    email: string;
  }>({ open: false, userId: '', email: '' });
  const pageSize = 100;
  const { toast } = useToast();

  const fetchUsers = async (page: number, search: string = '', filterPremium: string = 'all', sort_column: string = 'created_at', sort_direction: 'asc' | 'desc' = 'desc') => {
    try {
      setLoading(true);
      const offset = (page - 1) * pageSize;

      logger.info('Fetching users', { 
        page, 
        search: search || 'none',
        filterPremium,
        sort_column,
        sort_direction,
        pageSize, 
        offset 
      });

      // Fetch total count
      logger.debug('Calling get_auth_users_count RPC', { p_search: search || null });
      const { data: countData, error: countError } = await supabase
        .rpc('get_auth_users_count' as any, { p_search: search || null });

      if (countError) {
        logger.error('Error fetching user count', { 
          error: countError,
          code: countError.code,
          message: countError.message,
          details: countError.details,
          hint: countError.hint
        });
        throw countError;
      }

      logger.info('User count fetched', { count: countData });

      // Fetch users
      logger.debug('Calling get_auth_users RPC', { 
        p_search: search || null,
        p_limit: pageSize,
        p_offset: offset,
        p_sort_column: sort_column,
        p_sort_direction: sort_direction
      });
      
      const { data, error } = await supabase
        .rpc('get_auth_users' as any, {
          p_search: search || null,
          p_limit: pageSize,
          p_offset: offset,
          p_sort_column: sort_column,
          p_sort_direction: sort_direction
        });

      if (error) {
        logger.error('Error fetching users', { 
          error,
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        throw error;
      }

      logger.info('Users fetched successfully', { 
        userCount: (data as AuthUser[])?.length || 0,
        totalUsers: countData 
      });

      let filteredUsers = (data as AuthUser[]) || [];
      
      // Apply premium filter
      if (filterPremium === 'premium') {
        filteredUsers = filteredUsers.filter(u => u.has_subscription);
        // Sort by created_at descending (latest first)
        filteredUsers.sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      }

      setUsers(filteredUsers);
      setTotalUsers(filterPremium === 'premium' ? filteredUsers.length : ((countData as number) || 0));
    } catch (error: any) {
      logger.error('Failed to fetch users', { 
        error,
        page,
        search,
        errorMessage: error?.message,
        errorCode: error?.code
      });
      
      toast({
        title: 'Error Loading Users',
        description: error?.message || 'Failed to load users. Please check console for details.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(currentPage, searchQuery, premiumFilter, sortColumn, sortDirection);
  }, [currentPage, premiumFilter, sortColumn, sortDirection]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchUsers(1, searchQuery, premiumFilter, sortColumn, sortDirection);
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
    setCurrentPage(1);
  };

  const SortIcon = ({ column }: { column: string }) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="h-4 w-4 ml-1 inline opacity-50" />;
    }
    return sortDirection === 'asc' ? 
      <ArrowUp className="h-4 w-4 ml-1 inline" /> : 
      <ArrowDown className="h-4 w-4 ml-1 inline" />;
  };

  const copyUserId = (userId: string) => {
    navigator.clipboard.writeText(userId);
    setCopiedId(true);
    toast({
      title: 'Copied!',
      description: 'User ID copied to clipboard',
    });
    setTimeout(() => setCopiedId(false), 2000);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast({
        title: 'Invalid Password',
        description: 'Password must be at least 6 characters',
        variant: 'destructive',
      });
      return;
    }

    setActionLoading(passwordDialog.userId);
    try {
      const response = await supabase.functions.invoke('admin-reset-user-password', {
        body: { 
          userId: passwordDialog.userId,
          newPassword 
        }
      });

      if (response.error) {
        throw response.error;
      }

      toast({
        title: 'Password Updated',
        description: `Password updated successfully for ${passwordDialog.email}`,
      });

      setPasswordDialog({ open: false, userId: '', email: '' });
      setNewPassword('');
    } catch (error: any) {
      logger.error('Error setting password', { error, userId: passwordDialog.userId });
      toast({
        title: 'Error',
        description: error?.message || 'Failed to update password',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelSubscription = async (userId: string, email: string) => {
    setActionLoading(userId);
    try {
      const response = await supabase.functions.invoke('admin-cancel-subscription', {
        body: { userId }
      });

      if (response.error) {
        throw response.error;
      }

      toast({
        title: 'Subscription Cancelled',
        description: `Subscription cancelled for ${email}`,
      });

      setConfirmDialog({ open: false, userId: '', email: '' });
      
      // Refresh users list
      await fetchUsers(currentPage, searchQuery, premiumFilter, sortColumn, sortDirection);
    } catch (error: any) {
      logger.error('Error cancelling subscription', { error, userId });
      toast({
        title: 'Error',
        description: error?.message || 'Failed to cancel subscription',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(null);
    }
  };

  const totalPages = Math.ceil(totalUsers / pageSize);

  const getRoleBadgeVariant = (role: string) => {
    switch (role?.toLowerCase()) {
      case 'admin':
        return 'destructive';
      case 'specialist':
        return 'default';
      case 'caregiver':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          User Management
        </CardTitle>
        <CardDescription>
          View and manage all registered users ({totalUsers} total)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search Bar and Filter */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by email or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              className="pl-9"
            />
          </div>
          <Select value={premiumFilter} onValueChange={setPremiumFilter}>
            <SelectTrigger className="w-[180px]">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Filter users" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Users</SelectItem>
              <SelectItem value="premium">Premium Only</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleSearch} disabled={loading}>
            Search
          </Button>
        </div>

        {/* Users Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            No users found
          </div>
        ) : (
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('email')}
                  >
                    Email
                    <SortIcon column="email" />
                  </TableHead>
                  <TableHead>Roles</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('has_subscription')}
                  >
                    Subscription
                    <SortIcon column="has_subscription" />
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('email_confirmed_at')}
                  >
                    Email Confirmed
                    <SortIcon column="email_confirmed_at" />
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('last_sign_in_at')}
                  >
                    Last Sign In
                    <SortIcon column="last_sign_in_at" />
                  </TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow 
                    key={user.id} 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setSelectedUser(user)}
                  >
                    <TableCell className="font-medium">
                      {user.email}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {user.roles.length > 0 ? (
                          user.roles.map((role) => (
                            <Badge key={role} variant={getRoleBadgeVariant(role)}>
                              {role}
                            </Badge>
                          ))
                        ) : (
                          <Badge variant="secondary">user</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {user.has_subscription ? (
                        <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="secondary">None</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.email_confirmed_at ? (
                        <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">
                          Verified
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">
                          Pending
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.last_sign_in_at
                        ? formatDistanceToNow(new Date(user.last_sign_in_at), { addSuffix: true })
                        : <span className="text-muted-foreground italic">Never</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPasswordDialog({
                              open: true,
                              userId: user.id,
                              email: user.email
                            });
                          }}
                          disabled={actionLoading === user.id}
                        >
                          {actionLoading === user.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <KeyRound className="h-4 w-4" />
                          )}
                        </Button>
                        {user.has_subscription && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmDialog({
                                open: true,
                                userId: user.id,
                                email: user.email
                              });
                            }}
                            disabled={actionLoading === user.id}
                          >
                            {actionLoading === user.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <XCircle className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Pagination Controls */}
        {totalUsers > pageSize && (
          <div className="flex items-center justify-between pt-4">
            <div className="text-sm text-muted-foreground">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalUsers)} of {totalUsers} users
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1 || loading}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <div className="flex items-center gap-1">
                <span className="text-sm font-medium">{currentPage}</span>
                <span className="text-sm text-muted-foreground">of {totalPages}</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages || loading}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      {/* Password Dialog */}
      <Dialog open={passwordDialog.open} onOpenChange={(open) => {
        setPasswordDialog({ ...passwordDialog, open });
        if (!open) setNewPassword('');
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set New Password</DialogTitle>
            <DialogDescription>
              Set a new password for {passwordDialog.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter new password (min 6 characters)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSetPassword();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setPasswordDialog({ open: false, userId: '', email: '' });
                setNewPassword('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSetPassword}
              disabled={actionLoading === passwordDialog.userId}
            >
              {actionLoading === passwordDialog.userId ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Setting...
                </>
              ) : (
                'Set Password'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Confirmation Dialog */}
      <AlertDialog open={confirmDialog.open} onOpenChange={(open) => 
        setConfirmDialog({ ...confirmDialog, open })
      }>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel User Subscription</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel all active subscriptions for {confirmDialog.email}? This action will cancel their subscription in Stripe.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleCancelSubscription(confirmDialog.userId, confirmDialog.email)}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
          </AlertDialogContent>
      </AlertDialog>

      {/* User Details Sheet */}
      <Sheet open={!!selectedUser} onOpenChange={(open) => !open && setSelectedUser(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>User Details</SheetTitle>
            <SheetDescription>
              Complete information about the selected user
            </SheetDescription>
          </SheetHeader>
          
          {selectedUser && (
            <div className="mt-6 space-y-6">
              {/* User ID */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">User ID</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-muted rounded text-xs font-mono break-all">
                    {selectedUser.id}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyUserId(selectedUser.id)}
                  >
                    {copiedId ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Email</Label>
                <div className="px-3 py-2 bg-muted rounded text-sm">
                  {selectedUser.email}
                </div>
              </div>

              {/* Full Name */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Full Name</Label>
                <div className="px-3 py-2 bg-muted rounded text-sm">
                  {selectedUser.full_name || <span className="text-muted-foreground italic">Not set</span>}
                </div>
              </div>

              {/* Roles */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Roles</Label>
                <div className="flex gap-1 flex-wrap">
                  {selectedUser.roles.length > 0 ? (
                    selectedUser.roles.map((role) => (
                      <Badge key={role} variant={getRoleBadgeVariant(role)}>
                        {role}
                      </Badge>
                    ))
                  ) : (
                    <Badge variant="secondary">user</Badge>
                  )}
                </div>
              </div>

              {/* Subscription Status */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Subscription Status</Label>
                <div>
                  {selectedUser.has_subscription ? (
                    <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">
                      Active Premium
                    </Badge>
                  ) : (
                    <Badge variant="secondary">Free</Badge>
                  )}
                </div>
              </div>

              {/* Stripe Customer ID */}
              {selectedUser.stripe_customer_id && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Stripe Customer ID</Label>
                  <div className="px-3 py-2 bg-muted rounded text-xs font-mono break-all">
                    {selectedUser.stripe_customer_id}
                  </div>
                </div>
              )}

              {/* Email Confirmation */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Email Verification</Label>
                <div>
                  {selectedUser.email_confirmed_at ? (
                    <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">
                      Verified
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">
                      Pending
                    </Badge>
                  )}
                </div>
              </div>

              {/* Last Sign In */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Last Sign In</Label>
                <div className="px-3 py-2 bg-muted rounded text-sm">
                  {selectedUser.last_sign_in_at
                    ? formatDistanceToNow(new Date(selectedUser.last_sign_in_at), { addSuffix: true })
                    : <span className="text-muted-foreground italic">Never</span>}
                </div>
              </div>

              {/* Created At */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Account Created</Label>
                <div className="px-3 py-2 bg-muted rounded text-sm">
                  {formatDistanceToNow(new Date(selectedUser.created_at), { addSuffix: true })}
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-2 pt-4 border-t">
                <Label className="text-sm font-medium">Actions</Label>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setSelectedUser(null);
                      setPasswordDialog({
                        open: true,
                        userId: selectedUser.id,
                        email: selectedUser.email
                      });
                    }}
                  >
                    <KeyRound className="h-4 w-4 mr-2" />
                    Reset Password
                  </Button>
                  {selectedUser.has_subscription && (
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => {
                        setSelectedUser(null);
                        setConfirmDialog({
                          open: true,
                          userId: selectedUser.id,
                          email: selectedUser.email
                        });
                      }}
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Cancel Subscription
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </Card>
  );
}
